import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface BuyerUser {
  id: string;
  email: string;
  companyName: string;
}

interface BuyerAuthContextValue {
  buyer: BuyerUser | null;
  loading: boolean;
  requestLink: (email: string) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
}

const BuyerAuthContext = createContext<BuyerAuthContextValue | undefined>(undefined);

export const useBuyerAuth = () => {
  const ctx = useContext(BuyerAuthContext);
  if (!ctx) throw new Error('useBuyerAuth must be used within BuyerAuthProvider');
  return ctx;
};

export const BuyerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [buyer, setBuyer] = useState<BuyerUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/portal/auth?action=me', { credentials: 'include' })
      .then(async res => {
        if (res.ok) setBuyer(await res.json());
        else setBuyer(null);
      })
      .catch(() => setBuyer(null))
      .finally(() => setLoading(false));
  }, []);

  const requestLink = useCallback(async (email: string) => {
    try {
      await fetch('/api/portal/auth?action=request-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      return { success: true };
    } catch {
      return { success: false };
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch('/api/portal/auth?action=logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    setBuyer(null);
  }, []);

  return (
    <BuyerAuthContext.Provider value={{ buyer, loading, requestLink, logout }}>
      {children}
    </BuyerAuthContext.Provider>
  );
};
